import type { DecideReason, FormBlock } from '@/kova/spec/types'
import type { ApiClient } from '@/lib/api'

import type { Caller } from './handles'
import {
  actorsAreTheSamePerson, secondPersonRefusal, submitWrite, writeRefusal,
} from './writes'

/**
 * The two decisions on a commission payout: countersign it, or refuse to pay.
 *
 * # Why this is a module rather than four hundred more lines of `referrals.ts`
 *
 * The same argument `amlcontrols.ts` makes in its own header. `referrals.ts`
 * reads — nine screens of it, and most of its length is about what a partner
 * row means when half its fields are absent. This decides, and the rules about
 * who may decide a payout are a different body of knowledge from the rules
 * about how the queue renders. Keeping them apart is what makes either
 * checkable.
 *
 * # Approving is not paying, and every sentence here has to hold that line
 *
 * The state machine permits submission from no other state, so approving is
 * what makes a commission payment INSTRUCTABLE — and that is all it is. The
 * money leaves later, through FR-16.2's submission, which has no admin route
 * and no control anywhere on this console. A screen that read as though the
 * money had gone would be law 2's optimistic UI wearing a different word, on
 * the one section where the thing being approved is a transfer to a third
 * party.
 *
 * So: no "paid" anywhere in this module, no provider reference, and no
 * idempotency key on either request. The key is on the payout row already,
 * minted when the cycle requested it, and is shown on the approval card — it
 * belongs to the instruction, not to our decision about it.
 *
 * # The asymmetry between the two is the backend's and is deliberate
 *
 * Approve passes `authz.Authorize` (invariant 8). Reject does not, and the
 * contract says why: *"refusing to pay instructs nothing and moves nothing.
 * Putting an engine in front of it would mean a checker who has spotted a
 * problem must wait for something to agree before they can stop the payment"* —
 * the same asymmetry the AML queue draws between escalating and closing.
 *
 * The consequence for this console is that only ONE of these two controls can
 * come back with a `step_up`, a `review` or a `refuse_to_submit`. That is the
 * reason they are two `form` blocks rather than one `decide` block with two
 * outcomes: `decide` is documented as the block for a judgement that mints no
 * `decision_ref` and in which law 14's vocabulary "must not appear", which is
 * the exact opposite of what an approval here can answer with.
 *
 * # Rejecting is not free, and the control says so
 *
 * *"REJECTING RELEASES THE ACCRUALS the payout claimed, so a corrected cycle
 * can sweep them. A rejected payout that kept its claims would leave the partner
 * owed money no later cycle could pick up."* That is the fact an operator needs
 * before the click, so it is on the control rather than in a release note.
 */

/**
 * `referral.payout.approve` — held by FINANCE and GROWTH, and both are named.
 *
 * Two roles, checked against `internal/staff/staff.go` rather than inferred
 * from the section: *"maker-checker needs two DIFFERENT PEOPLE to be reachable
 * at all: a permission held by one role in a team of one is a queue that never
 * clears, and the pressure that produces is exactly what makes somebody look
 * for a way round the rule."*
 *
 * Naming only one of them in the refusal would send an operator to ask the
 * wrong team, which is the failure law 18 exists to prevent — a disabled
 * control is shown so staff know the escalation path, and a path to somebody
 * who cannot grant it is not one.
 */
const PERM_DECIDE = 'referral.payout.approve'

const ROLE = 'Finance or Growth'

export function payoutApprovePath(id: number): string {
  return `/referral/payouts/${String(id)}/approve`
}

export function payoutRejectPath(id: number): string {
  return `/referral/payouts/${String(id)}/reject`
}

/**
 * What a payout is, as far as a control needs to know.
 *
 * Deliberately not the queue's own row type. A control needs the id, the state
 * and the maker; handing it the whole read is how a control starts depending on
 * a field whose absence the read handles and the control does not.
 *
 * `amount` is already formatted, with its currency, by the read that rendered
 * the row — law 8 admits no bare number and this module does not do money
 * formatting (Part III: it lives in `lib/money` and nowhere else).
 */
export interface PayoutStanding {
  readonly id: number
  readonly state: string
  /** `requested_by`, exactly as the queue row carries it. */
  readonly requestedBy: string
  readonly amount: string
  readonly partner: string
}

/**
 * Why this operator may not decide this payout, or `''` when they may.
 *
 * Four refusals, in the order they override each other, and the order is the
 * order in which an operator can do something about them: the cell, then the
 * role, then the payout's own state, then who they are.
 *
 * The last one is not about authority and cannot be granted — see
 * `secondPersonRefusal`. It is computed here, before the click, from
 * `requested_by` on the queue row, which is on the row for precisely this
 * purpose: *"an operator scanning the list knows which rows they personally
 * cannot approve before opening one."*
 *
 * # The comparison is case-insensitive, because the database's is
 *
 * `referral.SameActor` folds case over the trimmed string and migration 106's
 * CHECK constraints compare the same way. Comparing exactly here — which is
 * what the AML and break-glass controls correctly do, against a backend that
 * compares exactly — would enable the approve control for the maker whenever
 * the two rows differ by a letter's case. That failure is silent, and it fails
 * OPEN on the only rule standing between one operator and the programme's
 * money.
 */
export function payoutDecisionRefusal(
  caller: Caller, p: PayoutStanding, act: string, secondPersonAct: string,
): string {
  const session = writeRefusal(caller, PERM_DECIDE, ROLE, act)
  if (session !== '') return session

  if (p.state === '') {
    return 'Disabled — this read did not establish what state the payout is in, so this console cannot tell whether it is still awaiting a decision. Deciding a payment blind is not something to offer.'
  }
  if (p.state !== 'awaiting_approval') {
    return `Disabled — this payout is ${p.state}, not awaiting approval. Only a payout still waiting for a second person can be countersigned or refused; the backend answers a conflict for anything else, and the queue above is a moment old.`
  }

  return secondPersonRefusal(
    caller, p.requestedBy, 'requested this payout', secondPersonAct, actorsAreTheSamePerson,
  )
}

/**
 * Law 21: a reason code from a closed list, never free text.
 *
 * **This console's own list, and it is not the managed registry** — there is no
 * reason-code registry on the admin surface to read, `cf-reasons` says so on
 * screen, and presenting an invented list as managed is the worse failure
 * because the next operator quotes it as policy.
 *
 * Split by act, and the split is load-bearing rather than tidy. "Verification
 * has expired" is a reason to refuse a payment and is not a reason to approve
 * one; an audit row six months from now is read by somebody who has only the
 * code, and a code that contradicts the act it explains is worse than a blank.
 */
const APPROVE_REASONS: readonly DecideReason[] = [
  ['reviewed_and_agreed', 'Reviewed the cycle independently and it is right', ''],
  ['lines_reconcile_to_amount', 'The per-referral lines add up to the amount requested', ''],
  ['partner_verification_current', 'The partner’s verification is in force, not merely passed once', ''],
  ['cycle_within_policy', 'The cycle and its threshold match the scheme in force', ''],
]

const REJECT_REASONS: readonly DecideReason[] = [
  ['lines_do_not_reconcile', 'The lines do not add up to the amount requested', ''],
  ['verification_expired', 'The verification behind this payout has lapsed', ''],
  ['partner_under_investigation', 'A conduct question about this partner is open', ''],
  ['cycle_requested_in_error', 'The cycle was run against the wrong period or partner', ''],
  ['duplicate_of_another_payout', 'This commission is claimed by another payout', ''],
]

/**
 * Countersigning. The second person, and the only thing that makes a commission
 * payment instructable.
 *
 * `confirm` is the payout id rather than a word. The difference between
 * countersigning payout 118 and payout 119 is invisible to a dialog that asks
 * only whether you are sure, and these are two different partners being paid
 * two different amounts.
 */
export function approvePayoutForm(
  api: ApiClient, caller: Caller, p: PayoutStanding,
): FormBlock {
  const blocked = payoutDecisionRefusal(
    caller, p, 'Countersigning a commission payout', 'Countersigning it',
  )
  return {
    t: 'form',
    title: `Countersign payout ${String(p.id)}`,
    lead: `The second signature on ${p.amount} to ${p.partner}. It does not send the money: approving is what makes the payment instructable, and the transfer is submitted afterwards by the process that owns it. Nothing on this screen moves funds.`,
    fields: [],
    reasons: APPROVE_REASONS,
    reasonNote: 'Your own reading of the cycle, not a restatement of the request. The point of a second signature is a second review — the person who requested this already gave their reason, and it is on the card above.',
    auditLine: `admin.referral.payout.approve · actor · referral_payout:${String(p.id)} · reason`,
    cta: 'Countersign this payout',
    ctaNote: 'Goes through the authorisation seam, which RE-READS the partner’s verification as it stands now — the check shown on the row is as at request and is not a statement about today. A verification that has expired since is refused there, with a decision reference, and nothing is written.',
    blocked,
    confirm: String(p.id),
    ...(blocked === '' ? { submit: approveSubmit(api, p) } : {}),
  }
}

function approveSubmit(api: ApiClient, p: PayoutStanding) {
  return (v: Readonly<Record<string, string>>) => submitWrite(
    () => api.post<unknown>(payoutApprovePath(p.id), { reason: v['reason'] ?? '' }),
    'The payout was countersigned',
    (answer) => {
      const state = stringField(answer, 'state')
      const ref = stringField(answer, 'approval_decision_ref')
      const lines: (readonly [string, string, string])[] = [
        ['payout', String(p.id), state === '' ? 'countersigned' : `now ${state}`],
      ]
      if (ref !== '') {
        lines.push(['decision_ref', ref, 'the decision that permitted this — quote it in the explorer'])
      }
      lines.push(['what happens next', 'the transfer is submitted by the process that owns it', 'nothing here has sent it'])
      return lines
    },
  )
}

/**
 * Refusing to pay.
 *
 * No seam, and the control says so — a checker who has spotted a problem does
 * not wait for an engine to agree before stopping a payment. The maker-checker
 * bar still applies: a maker cannot reject their own request either, which is
 * the rule people expect to apply only to approvals.
 */
export function rejectPayoutForm(
  api: ApiClient, caller: Caller, p: PayoutStanding,
): FormBlock {
  const blocked = payoutDecisionRefusal(
    caller, p, 'Rejecting a commission payout', 'Rejecting it',
  )
  return {
    t: 'form',
    title: `Reject payout ${String(p.id)}`,
    lead: `Refuses ${p.amount} to ${p.partner}. Nothing is instructed and nothing moves — which is why this passes no authorisation engine: a checker who has seen a problem should not have to wait for something to agree before stopping a payment.`,
    fields: [],
    reasons: REJECT_REASONS,
    reasonNote: 'Read by whoever runs the corrected cycle, and by the partner’s account manager if they ask why this month was late.',
    auditLine: `admin.referral.payout.reject · actor · referral_payout:${String(p.id)} · reason`,
    cta: 'Reject this payout',
    ctaNote: 'The commission this payout claimed is RELEASED, so a corrected cycle can sweep it — the partner is not written off, and the money is still owed. A rejected payout that kept its claims would leave a real debt that no later cycle could pick up and that would show on no report.',
    blocked,
    confirm: String(p.id),
    ...(blocked === '' ? { submit: rejectSubmit(api, p) } : {}),
  }
}

function rejectSubmit(api: ApiClient, p: PayoutStanding) {
  return (v: Readonly<Record<string, string>>) => submitWrite(
    () => api.post<unknown>(payoutRejectPath(p.id), { reason: v['reason'] ?? '' }),
    'The payout was rejected',
    (answer) => {
      const state = stringField(answer, 'state')
      return [
        ['payout', String(p.id), state === '' ? 'rejected' : `now ${state}`],
        ['the commission', 'released', 'claimable again by a corrected cycle — still owed, not written off'],
      ]
    },
  )
}

/** One string off an answer whose shape is not otherwise this module's business. */
function stringField(answer: unknown, key: string): string {
  if (typeof answer !== 'object' || answer === null) return ''
  const value = (answer as Record<string, unknown>)[key]
  return typeof value === 'string' ? value : ''
}

/**
 * Both controls, for every payout still awaiting a decision.
 *
 * One pair per payout and never one control taking an id — the reason #157 gave
 * for restrict/restore holds harder here: a mistyped id pays or refuses a
 * partner who is not on the screen, and there is no undo on either.
 *
 * Nothing is truncated. A payout left off the screen to keep it short is one
 * nobody countersigns, and the queue's own read makes the same promise.
 */
export function payoutControls(
  api: ApiClient, caller: Caller, awaiting: readonly PayoutStanding[],
): readonly FormBlock[] {
  return awaiting.flatMap((p) => [
    approvePayoutForm(api, caller, p),
    rejectPayoutForm(api, caller, p),
  ])
}
